import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

const CAT_KEYS = ["immune", "focus", "energy", "performance", "vitality", "relaxation"];

export default function Shop() {
  const { t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const active = params.get("cat") || "all";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .products(active === "all" ? undefined : active)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [active]);

  const setCat = (cat) => {
    if (cat === "all") setParams({});
    else setParams({ cat });
  };

  return (
    <div data-testid="shop-page" className="bg-white">
      <section className="relative overflow-hidden border-b border-navy/10 bg-surface pb-16 pt-28">
        <span aria-hidden className="text-ghost pointer-events-none absolute -bottom-8 end-0 select-none font-display text-[16vw] italic leading-none">
          {t.shop.title}
        </span>
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal><p className="label-luxe text-gold-dark">{t.shop.kicker}</p></Reveal>
          <Reveal delay={1}>
            <h1 className="mt-5 font-display text-5xl font-light tracking-tight text-navy sm:text-6xl">{t.shop.title}</h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-5 text-sm font-light text-navy/50">{t.shop.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <div data-testid="shop-filters" className="mb-12 flex flex-wrap gap-3">
          <button
            data-testid="filter-all"
            onClick={() => setCat("all")}
            className={`border px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${
              active === "all" ? "border-navy bg-navy text-white" : "border-navy/15 text-navy/60 hover:border-gold hover:text-gold-dark"
            }`}
          >
            {t.shop.all}
          </button>
          {CAT_KEYS.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c}`}
              onClick={() => setCat(c)}
              className={`border px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${
                active === c ? "border-navy bg-navy text-white" : "border-navy/15 text-navy/60 hover:border-gold hover:text-gold-dark"
              }`}
            >
              {t.cats[`${c}_n`]}
            </button>
          ))}
        </div>

        <p data-testid="shop-results-count" className="mb-8 text-xs font-light text-navy/40">
          {products.length} {t.shop.results}
        </p>

        {loading ? (
          <p data-testid="shop-loading" className="py-20 text-center text-sm text-navy/40">{t.common.loading}</p>
        ) : products.length === 0 ? (
          <p data-testid="shop-empty" className="py-20 text-center text-sm text-navy/40">{t.shop.empty}</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
